import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

/**
 * Modal para capturar el motivo de un rechazo (en vez de un prompt del navegador).
 * Se renderiza en un CDK Overlay (a nivel de body) para no quedar confinado ni
 * tapado por el layout del dashboard.
 */
@Component({
  selector: 'app-reject-reason-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <ng-template #tpl>
      <div class="bg-white rounded-3xl shadow-2xl w-[92vw] max-w-md">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 class="text-lg font-bold text-gray-800">{{ title }}</h2>
          <button (click)="onCancel()"
            class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-all">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="px-6 py-5 flex flex-col gap-3">
          <label class="text-sm text-gray-600">Motivo (se mostrará al alumno)</label>
          <textarea [(ngModel)]="reason" rows="3"
            class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Explica el motivo del rechazo…"></textarea>
          <div class="flex justify-end gap-2">
            <button (click)="onCancel()"
              class="text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button (click)="onConfirm()" [disabled]="!reason.trim()"
              class="text-sm px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-600 disabled:opacity-40">Rechazar</button>
          </div>
        </div>
      </div>
    </ng-template>
  `,
})
export class RejectReasonModalComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() title = 'Rechazar documento';
  @Output() confirm = new EventEmitter<string>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('tpl') tpl!: TemplateRef<any>;

  reason = '';
  private overlayRef?: OverlayRef;

  constructor(private overlay: Overlay, private vcr: ViewContainerRef) {}

  ngOnChanges(): void {
    if (this.visible) {
      this.open();
    } else {
      this.close();
    }
  }

  private open(): void {
    if (this.overlayRef || !this.tpl) {
      return;
    }
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      hasBackdrop: true,
      backdropClass: 'modal-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
    this.overlayRef.backdropClick().subscribe(() => this.onCancel());
    this.overlayRef.attach(new TemplatePortal(this.tpl, this.vcr));
  }

  private close(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }

  onConfirm(): void {
    const reason = this.reason.trim();
    if (reason) {
      this.confirm.emit(reason);
      this.reason = '';
    }
  }

  onCancel(): void {
    this.reason = '';
    this.cancel.emit();
  }

  ngOnDestroy(): void {
    this.close();
  }
}
